import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useGetShopSettingQuery, useSaveShopSettingMutation } from '../../api/billing/BillApi';
import { MasterRow } from '../../types/master';
import { useSnackbar } from '../../utils/snackbar';
import { can } from '../../utils/auth';
import { digitsOnly, lowerEmail } from '../../utils/clean';
import { GST_STATES, stateNameFor } from '../../utils/states';

/** One labelled value on the card. Long text is clamped and shown in full on hover. */
const ProfileField = ({ label, value, clamp }: { label: string; value?: unknown; clamp?: number }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
      {label}
    </Typography>
    <Tooltip title={clamp && value ? String(value) : ''} arrow placement="top-start">
      <Typography
        sx={{
          overflowWrap: 'anywhere',
          ...(clamp ? { display: '-webkit-box', WebkitLineClamp: clamp, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : null),
        }}
      >
        {value === undefined || value === null || value === '' ? '-' : String(value)}
      </Typography>
    </Tooltip>
  </Grid>
);

/**
 * The shop's own profile - the card the owner shows a bank or a supplier, and the
 * details printed at the top of every bill.
 *
 * <p>Laid out the way the retail suite lays out a company profile: the shop's name
 * across the top, the details beneath as labelled values, and one Edit button that
 * opens the form. Reading is the common case; editing happens twice a year, so the
 * boxes do not need to be on screen the rest of the time.
 */
const ShopSettings = () => {
  const { openSnackbar } = useSnackbar();
  const { data } = useGetShopSettingQuery();
  const [saveShopSetting, { isLoading: saving }] = useSaveShopSettingMutation();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<MasterRow>({});
  const dirty = useRef(false);

  useEffect(() => {
    // Only fill the form from the server until the shop starts typing - a refetch
    // must not overwrite what they are part way through entering.
    if (data && !dirty.current) setForm(data);
  }, [data]);

  const shop = (data || {}) as Record<string, unknown>;
  const mayEdit = can('SETTINGS', 'edit');

  const address = [shop.addressLine1, shop.addressLine2, shop.city, shop.state]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean)
    .join(', ');
  const fullAddress = shop.zipCode ? `${address}${address ? ' - ' : ''}${shop.zipCode}` : address;

  const openForm = () => {
    setForm(data || {});
    dirty.current = false;
    setOpen(true);
  };

  const field = (key: string, label: string, helper?: string, md = 4) => {
    // The same two rules as everywhere else: a phone is digits, an email is lower
    // case. Applied as they type so the box explains itself.
    const isPhone = key === 'phone' || key === 'zipCode';
    const isEmail = key === 'email';
    return (
      <Grid item xs={12} sm={6} md={md}>
        <TextField
          label={label}
          size="small"
          fullWidth
          type={isEmail ? 'email' : 'text'}
          inputProps={isPhone ? { inputMode: 'numeric', maxLength: key === 'zipCode' ? 6 : 15 } : undefined}
          helperText={helper}
          value={(form[key] as string) ?? ''}
          onChange={(event) => {
            dirty.current = true;
            const raw = event.target.value;
            const value = isPhone ? digitsOnly(raw, key === 'zipCode' ? 6 : 15) : isEmail ? lowerEmail(raw) : raw;
            setForm((prev) => ({ ...prev, [key]: value }));
          }}
        />
      </Grid>
    );
  };

  /**
   * The shop's own state, picked rather than typed, setting the name and the code
   * together.
   *
   * <p>They were two free-text boxes, and a shop that typed "tamilnadu" into the name
   * and left the code empty was told nothing. That code is what decides whether a sale
   * is CGST + SGST or IGST; with it empty the software cannot tell whether a sale
   * crosses a border, so every bill came out local - sales to other states included -
   * and nothing on screen ever said why.
   */
  const stateField = (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        select
        label="State"
        size="small"
        fullWidth
        required
        error={!form.stateCode}
        value={(form.stateCode as string) ?? ''}
        helperText={
          form.stateCode
            ? 'Sales to any other state are taxed as IGST'
            : 'Needed for GST. Without it, every sale is taxed as a local one.'
        }
        onChange={(event) => {
          dirty.current = true;
          const code = event.target.value;
          setForm((prev) => ({ ...prev, stateCode: code, state: stateNameFor(code) ?? '' }));
        }}
      >
        {GST_STATES.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.code} &nbsp;{option.name}
          </MenuItem>
        ))}
      </TextField>
    </Grid>
  );

  const save = async () => {
    if (!String(form.shopName || '').trim()) {
      openSnackbar('Shop name is required', 'warning');
      return;
    }
    if (!String(form.stateCode || '').trim()) {
      openSnackbar('Choose the state the shop is in - GST cannot be worked out without it', 'warning');
      return;
    }
    try {
      await saveShopSetting(form).unwrap();
      dirty.current = false;
      setOpen(false);
      openSnackbar('Shop details saved', 'success');
    } catch (error: any) {
      openSnackbar(error?.data?.message || 'Could not save the shop details', 'error');
    }
  };

  return (
    <Box>
      <Card sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} textTransform="uppercase">
              {String(shop.shopName || 'Your shop')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Printed at the top of every bill
            </Typography>
          </Box>
          {mayEdit && (
            <Button variant="outlined" startIcon={<ModeEditOutlineOutlinedIcon />} onClick={openForm} sx={{ borderRadius: 2 }}>
              Edit
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <ProfileField label="Contact Person" value={shop.contactPersonName} />
          <ProfileField label="Phone" value={shop.phone} />
          <ProfileField label="Email" value={shop.email} />
          <ProfileField label="GSTIN" value={shop.gstin} />
          <ProfileField label="PAN Number" value={shop.panNumber} />
          <ProfileField label="Website" value={shop.website} />
          <ProfileField label="State Code" value={shop.stateCode} />
          <ProfileField label="Bill Number Looks Like" value={shop.billPrefix ? `${shop.billPrefix}/2026-27/001` : undefined} />
          <ProfileField label="Default GST %" value={shop.gstPercentage} />
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
              Address
            </Typography>
            <Typography sx={{ overflowWrap: 'anywhere' }}>{fullAddress || '-'}</Typography>
          </Grid>
        </Grid>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography fontWeight={700} fontSize="1rem">
            Edit Shop Details
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1.5, mt: 1 }}>
            Shop
          </Typography>
          <Grid container spacing={2}>
            {field('shopName', 'Shop Name')}
            {field('contactPersonName', 'Contact Person')}
            {field('phone', 'Phone')}
            {field('email', 'Email')}
            {field('website', 'Website')}
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mt: 3, mb: 1.5 }}>
            Address
          </Typography>
          <Grid container spacing={2}>
            {field('addressLine1', 'Address Line 1', undefined, 6)}
            {field('addressLine2', 'Address Line 2', undefined, 6)}
            {field('city', 'City')}
            {stateField}
            {field('zipCode', 'Pincode')}
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mt: 3, mb: 1.5 }}>
            Tax and billing
          </Typography>
          <Grid container spacing={2}>
            {field('gstin', 'GSTIN')}
            {field('panNumber', 'PAN Number')}
            {field('billPrefix', 'Bill Prefix', 'Bills come out as KM/2026-27/001, restarting each April')}
            {field('gstPercentage', 'Default GST %', 'Used when a product is saved without its own rate')}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={saving} onClick={save}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopSettings;
